import { useCallback, useMemo, useRef, useState } from 'react';
import { GlossaryTooltipProps } from '@tryhackme/thm-ui-components/dist/components/atoms/glossary-tooltip/glossary-tooltip.types';
import { ArrowPosition } from '@tryhackme/thm-ui-components/dist/components/atoms/tooltip/tooltip.types';

import { GlossaryEntry } from 'src/features/glossary/glossary.types';

import { AccordionDetailsProps } from '../../accordion-details/accordion-details.types';

export const useRoomGlossaryTooltip = (
  parentElementId: string,
  glossaryData: AccordionDetailsProps['glossaryData'],
) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [arrowPosition, setArrowPosition] = useState<ArrowPosition>('top-center');
  const [activeGlossary, setActiveGlossary] = useState<GlossaryEntry | null>(null);
  const tooltipProps = useMemo(() => getTooltipProps(activeGlossary, arrowPosition), [activeGlossary, arrowPosition]);

  const handleClick: React.MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      const target = event.target as HTMLElement;

      const term = target.textContent;
      const glossaryEntry = glossaryData.find((entry) => entry.term === term);

      if (!glossaryEntry) {
        setActiveGlossary(null);
        return;
      }
      setActiveGlossary(glossaryEntry);

      const childRect = target.getBoundingClientRect();
      const parentRect = document.querySelector(`#${parentElementId}`)?.getBoundingClientRect();

      if (!parentRect) return;

      let x = childRect.x - (parentRect.x + childRect.width / 2);
      const y = childRect.y - parentRect.y - 16;

      if (x < 180) {
        setArrowPosition('bottom-left');
      } else if (x + 180 > parentRect.width) {
        x -= 260;
        setArrowPosition('bottom-right');
      } else {
        x -= 130;
        setArrowPosition('bottom-center');
      }

      setPosition({ x, y });
    },
    [glossaryData, parentElementId],
  );

  return {
    contentRef,
    activeGlossary,
    setActiveGlossary,
    position,
    tooltipProps,
    arrowPosition,
    handleClick,
  };
};

const getTooltipProps = (glossaryEntry: GlossaryEntry | null, arrowPosition: ArrowPosition = 'top-left') => {
  const props: GlossaryTooltipProps = {
    arrowPosition,
    tooltipTitle: glossaryEntry?.term || '',
    textContent: glossaryEntry?.definition || '',
  };

  if (glossaryEntry?.resources?.videos?.length) {
    props.videoContent = glossaryEntry.resources.videos;
  }

  if (glossaryEntry?.resources?.articles.length) {
    props.articleContent = glossaryEntry.resources.articles;
  }

  if (glossaryEntry?.resources?.roomCodes.length) {
    props.roomContent = glossaryEntry.resources.roomCodes.map((roomCode) => ({
      code: roomCode,
      imageURL: '',
      description: '',
      href: `https://tryhackme.com/room/${roomCode.trim()}`,
    }));
  }

  return props;
};
